// src/pages/auths/AdminLogin.tsx
import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import { signInWithEmailAndPassword } from 'firebase/auth'; 
import { doc, getDoc, getDocs, collection } from 'firebase/firestore'; 
import { auth, db } from '../../firebase'; // Assuming these are correctly exported

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  background-color: #f5f5f5;
  font-family: Arial, sans-serif;
`;

const LoginBox = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  color: #333;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const InputGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: bold;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  box-sizing: border-box;
`;

const Button = styled.button<{$loading?: boolean}>`
  padding: 0.75rem;
  background-color: ${props => props.$loading ? '#aaa' : '#4CAF50'};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: ${props => props.$loading ? 'not-allowed' : 'pointer'};
  transition: background-color 0.3s;
`;

const Error = styled.div`
  color: #d32f2f;
  background-color: #ffebee;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  text-align: center;
`;

// 🔑 FIX: Update the AdminLoginProps interface to expect the 'role' string
interface AdminLoginProps {
  onLogin: (role: 'admin' | 'moderator' | 'superadmin') => void; // Changed from (status: boolean)
}

interface UserData {
    role: 'admin' | 'moderator' | 'superadmin';
    isActive: boolean;
}

const AdminLogin: React.FC<AdminLoginProps> = ({onLogin}) => {
  const [credentials, setCredentials] = useState({
    email: '', 
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [checkingAdmins, setCheckingAdmins] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdmins = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'admins'));
        if (querySnapshot.empty) {
          navigate('/admin/setup');
        }
      } catch (err) {
        console.error('Error checking admins:', err);
      } finally {
        setCheckingAdmins(false);
      }
    };
    checkAdmins();
  }, [navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
   });
    if (error) setError('');
 };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth, 
        credentials.email, 
        credentials.password
      );      
      const user = userCredential.user;

      const userDocRef = doc(db, 'admins', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        await auth.signOut();
        setError('Access denied. You are not a registered Admin or Moderator.');
        setIsLoading(false); 
        return; 
      }      
      const userData = userDocSnap.data() as UserData;

      if (userData.isActive === false) {
        await auth.signOut();
        setError('Your account is currently inactive. Contact support.');
        setIsLoading(false); 
        return; 
      }
      
// 4. Role Check and Redirection
       const userRole = userData.role.toLowerCase(); 

       if (userRole === 'admin' || userRole === 'superadmin') {
         // 🔑 FIX: Pass the role string instead of true
         onLogin('admin'); 
         navigate('/admin');
       } else if (userRole === 'moderator') {
         // 🔑 FIX: Pass the role string instead of true
         onLogin('moderator'); 
         navigate('/moderator');
       } else {
        await auth.signOut();
        setError('Your account role is not recognized. Access denied.');
        setIsLoading(false); 
        return; 
      }

    } catch (err: any) {
      console.error('Login error:', err);      
      let errorMessage = 'Login failed. Please try again.';
      
      // Handle common Firebase Auth errors
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password.';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Access temporarily blocked due to too many failed attempts.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
 };

  return (
    <Container>
      <LoginBox>
        {checkingAdmins ? (
          <div style={{textAlign: 'center', padding: '2rem'}}>
            Checking admin accounts...
          </div>
        ) : (
          <>
            <Title>Staff Login</Title>
            <Form onSubmit={handleSubmit}>
              <InputGroup>
                <Label htmlFor="email">Email</Label> 
                <Input
                  type="email" 
                  id="email"
                  name="email"
                  value={credentials.email}
                  onChange={handleChange}
                  placeholder="Enter staff email address"
                  required
                />
              </InputGroup>
              
              <InputGroup>
                <Label htmlFor="password">Password</Label>
                <Input
                  type="password"
                  id="password"
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  required
                />
              </InputGroup>          
              {error && <Error>{error}</Error>}          
              <Button 
                type="submit" 
                $loading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Verifying Credentials...' : 'Login'}
              </Button>
            </Form>
          </>
        )}
      </LoginBox>
    </Container>
  );
};

export default AdminLogin;